import React, { useEffect, useState } from "react";
import { Box, Paper, useTheme } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import { observer } from "mobx-react";
import { useLocation, useNavigate } from "react-router-dom";
import { useNotification } from "../../../../../utils/NotificationsContext";
import { SortType } from "../../../../../models/SortType";
import {
  parseQueryParamsForWaitlistFilters,
  WaitlistFilters,
} from "../../../../../models/Swayy/WaitlistFilters";
import { SwayyWaitlistStore } from "../../../stores/SwayyWaitlistStore";
import AppButton from "../../../../../views/components/common/buttons/AppButton";
import CustomTypography from "../../../../../views/components/common/form/CustomTypography";
import WaitlistTable from "../../components/swayy/WaitlistTable";

const waitlistStore = new SwayyWaitlistStore();

const quickAccessCardSx = {
  borderRadius: "12px",
  border: "1px solid #E2E8F0",
  bgcolor: "#FFFFFF",
  overflow: "hidden",
  boxShadow: "0 1px 3px rgba(0,0,0,0.04)",
} as const;

const WaitlistListing: React.FC = () => {
  const theme = useTheme();
  const { showError } = useNotification();
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);

  const initialFilters = parseQueryParamsForWaitlistFilters(null, searchParams);
  const [filters, setFilters] = useState<WaitlistFilters>(initialFilters);
  const [isLoading, setIsLoading] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  useEffect(() => {
    setIsLoading(true);
    waitlistStore
      .getWaitlist(filters)
      .catch((err) =>
        showError(
          "Error",
          err?.response?.data?.message ||
          "Something went wrong! Please try again later.",
        ),
      )
      .finally(() => setIsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  useEffect(() => {
    const nextSearchParams = new URLSearchParams();
    Object.entries(filters).forEach(([key, value]) => {
      if (value !== undefined) {
        nextSearchParams.set(key, value.toString());
      }
    });
    const queryString = nextSearchParams.toString();
    if (queryString) {
      navigate({ search: queryString }, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters, navigate]);

  const handlePageChange = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number,
  ) => {
    setFilters((prev) => ({ ...prev, page_no: newPage }));
  };

  const handleRowsPerPageChange = (newLimit: number) => {
    setFilters((prev) => ({ ...prev, limit: newLimit, page_no: 1 }));
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    try {
      const response = await waitlistStore.downloadWaitlist();
      if (response.data) {
        const downloadUrl = window.URL.createObjectURL(response.data);
        const link = document.createElement("a");
        link.href = downloadUrl;
        link.download = response.filename || "Waitlist.csv";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        window.URL.revokeObjectURL(downloadUrl);
      }
    } catch (err: any) {
      showError(
        "Error",
        err?.response?.data?.message ||
        "Failed to download waitlist. Please try again later.",
      );
    } finally {
      setIsDownloading(false);
    }
  };

  return (
    <Paper sx={quickAccessCardSx}>
      <Box
        sx={{
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          gap: 2,
          px: 2,
          py: 2,
          borderBottom: "1px solid #F1F5F9",
        }}
      >
        <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
          <CustomTypography variant="heading3">Swayy Waitlist</CustomTypography>
          <CustomTypography
            variant="body2"
            color={theme.palette.customColors?.greyTextColor}
          >
            View waitlist sign-ups from Swayy.
          </CustomTypography>
        </Box>

        {waitlistStore.total_count > 0 && (
          <AppButton
            variant="primary"
            label={isDownloading ? "Downloading..." : "Download"}
            startIcon={<DownloadIcon />}
            disabled={isDownloading}
            onClick={handleDownload}
            customStyles={{ minWidth: 120 }}
          />
        )}
      </Box>

      <WaitlistTable
        loading={isLoading}
        waitlist={waitlistStore.waitlist}
        limit={waitlistStore.limit}
        page_no={filters.page_no}
        total_count={waitlistStore.total_count}
        sortBy={filters.sort_by}
        sortType={filters.sort_type}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        onSortChange={(column, sortType) => {
          setFilters((prev) => ({
            ...prev,
            sort_by: column,
            sort_type: sortType,
            page_no: 1,
          }));
        }}
      />
    </Paper>
  );
};

export default observer(WaitlistListing);
